// components/CustomerLoyaltyDashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Button,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  AppBar,
} from '@mui/material';
import {
  Star,
  Diamond,
  EmojiEvents,
  TrendingUp,
  People,
  AttachMoney,
  LocalBar,
  Hotel,
  Build,
  FilterList,
  Download,
  Print,
  Refresh,
  CalendarToday,
  Email,
  Phone,
  Flag,
  Loyalty,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../../components/sidebars/Sidebar_hotelAdmin';
import TopNav from '../../../components/TopNav_hotelAdmin';

// Styled Components
const LoyaltyCard = styled(Card)(({ theme, tier }) => {
  const getTierColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'platinum': return theme.palette.primary.main;
      case 'gold': return theme.palette.warning.main;
      case 'silver': return theme.palette.grey[600];
      case 'bronze': return theme.palette.secondary.main;
      default: return theme.palette.info.main;
    }
  };

  return {
    borderLeft: `4px solid ${getTierColor(tier)}`,
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[8],
    },
  };
});

const TierBadge = styled(Chip)(({ theme, tier }) => {
  const getTierStyle = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'platinum':
        return {
          background: 'linear-gradient(135deg, #E5E4E2 0%, #B9B9B9 100%)',
          color: '#333',
          fontWeight: 'bold',
        };
      case 'gold':
        return {
          background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
          color: '#333',
          fontWeight: 'bold',
        };
      case 'silver':
        return {
          background: 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)',
          color: '#333',
          fontWeight: 'bold',
        };
      case 'bronze':
        return {
          background: 'linear-gradient(135deg, #CD7F32 0%, #8C4A1F 100%)',
          color: 'white',
          fontWeight: 'bold',
        };
      default:
        return {
          backgroundColor: theme.palette.primary.light,
          color: 'white',
        };
    }
  };

  const style = getTierStyle(tier);

  return {
    background: style.background,
    color: style.color,
    fontWeight: style.fontWeight,
    border: `2px solid ${style.background}`,
  };
});

const ScoreProgress = styled(LinearProgress)(({ theme, score }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[200],
  '& .MuiLinearProgress-bar': {
    borderRadius: 4,
    backgroundColor: score >= 80 ? theme.palette.success.main : 
                   score >= 60 ? theme.palette.warning.main : 
                   theme.palette.primary.main,
  },
}));

const RankBadge = styled(Box)(({ theme, rank }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 'bold',
  fontSize: '14px',
  color: 'white',
  backgroundColor: 
    rank === 1 ? '#FFD700' : 
    rank === 2 ? '#C0C0C0' : 
    rank === 3 ? '#CD7F32' : 
    theme.palette.primary.main,
}));

// Helper functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN'
  }).format(amount || 0);
};

const formatNumber = (number) => {
  return new Intl.NumberFormat().format(number || 0);
};

const getUnitIcon = (unit) => {
  switch (unit) {
    case 'reception': return <Hotel />;
    case 'restaurant_bar': return <LocalBar />;
    case 'facility_management': return <Build />;
    case 'overall': return <EmojiEvents />;
    default: return <People />;
  }
};

const getUnitTitle = (unit) => {
  switch (unit) {
    case 'reception': return 'Accommodation';
    case 'restaurant_bar': return 'Restaurant & Bar';
    case 'facility_management': return 'Facility Management';
    case 'overall': return 'Overall Loyalty';
    default: return unit;
  }
};

// Customer Detail Dialog
const CustomerDetailDialog = ({ open, customer, unit, onClose }) => {
  if (!customer) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            {customer.customer_name?.split(' ').map(n => n[0]).join('')}
          </Avatar>
          <Box>
            <Typography variant="h6">{customer.customer_name}</Typography>
            <Typography variant="body2" color="textSecondary">
              {getUnitTitle(unit)} • Rank #{customer.rank}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Contact Information</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Email color="action" />
                    <Typography variant="body2">{customer.email}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Phone color="action" />
                    <Typography variant="body2">{customer.phone}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Flag color="action" />
                    <Typography variant="body2">{customer.nationality || 'Not specified'}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Loyalty Status</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Tier</Typography>
                    <TierBadge tier={customer.tier} label={customer.tier} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Loyalty Score</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {customer.loyalty_score}/100
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Points</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {formatNumber(customer.loyalty_points || 0)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary" fontWeight="bold">
                        {formatCurrency(customer.total_spent)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Spent
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="secondary" fontWeight="bold">
                        {formatNumber(customer.total_stays || customer.total_orders || customer.total_bookings || 0)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total {unit === 'reception' ? 'Stays' : unit === 'restaurant_bar' ? 'Orders' : 'Bookings'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success" fontWeight="bold">
                        {formatCurrency(customer.avg_booking_value || customer.avg_order_value || 0)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Average Value
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="info" fontWeight="bold">
                        {customer.visit_count || customer.unique_items_ordered || customer.unique_facilities_used || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {unit === 'restaurant_bar' ? 'Unique Items' : unit === 'facility_management' ? 'Facilities Used' : 'Visits'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {customer.last_visit && (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Last visit: {new Date(customer.last_visit).toLocaleDateString()} • 
                Member since: {new Date(customer.member_since).toLocaleDateString()}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Customer Rankings Table
const CustomerRankingsTable = ({ customers, unit, onCustomerClick }) => {
  return (
    <TableContainer component={Paper} elevation={2}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width="60px">Rank</TableCell>
            <TableCell>Customer</TableCell>
            <TableCell align="right">Total Spent</TableCell>
            <TableCell align="center">Visits</TableCell>
            <TableCell align="center">Tier</TableCell>
            <TableCell align="center">Loyalty Score</TableCell>
            <TableCell align="right">Last Visit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {customers.map((customer) => (
            <TableRow 
              key={customer.id} 
              sx={{ 
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
              onClick={() => onCustomerClick(customer)}
            >
              <TableCell>
                <RankBadge rank={customer.rank}>
                  {customer.rank}
                </RankBadge>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    {customer.customer_name?.split(' ').map(n => n[0]).join('')}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {customer.customer_name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {customer.email}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body1" fontWeight="bold" color="primary">
                  {formatCurrency(customer.total_spent)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Avg: {formatCurrency(customer.avg_booking_value || customer.avg_order_value || 0)}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Chip 
                  label={formatNumber(customer.total_stays || customer.total_orders || customer.total_bookings || 0)}
                  color="secondary"
                  variant="outlined"
                  size="small"
                />
              </TableCell>
              <TableCell align="center">
                <TierBadge tier={customer.tier} label={customer.tier} size="small" />
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScoreProgress 
                    variant="determinate" 
                    value={customer.loyalty_score} 
                    sx={{ flexGrow: 1 }}
                  />
                  <Typography variant="body2" fontWeight="bold" minWidth="35px">
                    {customer.loyalty_score}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">
                  {customer.last_visit ? new Date(customer.last_visit).toLocaleDateString() : 'Never'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Main Dashboard Component
const CustomerLoyaltyDashboard = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('overall');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [limit, setLimit] = useState(20);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const getHotelCode = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.hotelcode || storedProfile.hotel_code || '';
    } catch (error) {
      console.error('Error getting hotel code:', error);
      return '';
    }
  };

  const loadLoyaltyData = async (unit = selectedUnit) => {
    setLoading(true);
    setError('');
    
    try {
      const hotelCode = getHotelCode();
      if (!hotelCode) {
        throw new Error('Hotel code not found');
      }

      const response = await axios.get(`${API_BASE_URL}/hotel/admin/customer_loyalty.php`, {
        params: {
          hotel_code: hotelCode,
          unit: unit === 'overall' ? null : unit,
          limit: limit
        }
      });

      if (response.data.success) {
        setLoyaltyData(response.data);
      } else {
        throw new Error(response.data.error || 'Failed to load loyalty data');
      }
    } catch (err) {
      console.error('Error loading loyalty data:', err);
      setError(err.message);
      showSnackbar('Failed to load customer loyalty data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLoyaltyData();
  }, [limit]);

  const handleUnitChange = (event, newValue) => {
    setSelectedUnit(newValue);
    loadLoyaltyData(newValue);
  };

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    setDetailDialogOpen(true);
  };

  const handleExportCSV = () => {
    if (!loyaltyData?.rankings[selectedUnit]) {
      showSnackbar('No data available for export', 'warning');
      return;
    }

    const customers = loyaltyData.rankings[selectedUnit];
    const headers = ['Rank', 'Customer Name', 'Email', 'Phone', 'Total Spent', 'Visits', 'Tier', 'Loyalty Score', 'Last Visit'];
    
    const csvContent = [
      headers.join(','),
      ...customers.map(customer => [
        customer.rank,
        `"${customer.customer_name}"`,
        customer.email,
        customer.phone,
        customer.total_spent,
        customer.total_stays || customer.total_orders || customer.total_bookings,
        customer.tier,
        customer.loyalty_score,
        customer.last_visit
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `customer_loyalty_${selectedUnit}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showSnackbar('CSV exported successfully', 'success');
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const currentRankings = loyaltyData?.rankings[selectedUnit] || [];

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        {/* Top Navigation */}
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            navigate('/Entri');
          }}
          onNotificationClick={() => alert('You have new notifications!')}
          toggleSidebar={toggleSidebar}
        />

        {/* Header */}
        <Paper elevation={2} sx={{ p: 3, mt: 3, backgroundColor: 'primary.main', color: 'white', borderRadius: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            👑 Customer Loyalty Rankings
          </Typography>
          <Typography variant="subtitle1">
            Track and reward your most valuable customers across all hotel units
          </Typography>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
            {error}
            <Button onClick={() => loadLoyaltyData()} sx={{ ml: 2 }} size="small">
              Retry
            </Button>
          </Alert>
        )}

        {/* Controls */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Tabs value={selectedUnit} onChange={handleUnitChange}>
                <Tab icon={<EmojiEvents />} label="Overall" value="overall" />
                <Tab icon={<Hotel />} label="Accommodation" value="reception" />
                <Tab icon={<LocalBar />} label="Restaurant & Bar" value="restaurant_bar" />
                <Tab icon={<Build />} label="Facilities" value="facility_management" />
              </Tabs>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <TextField
                  size="small"
                  label="Show Top"
                  type="number"
                  value={limit}
                  onChange={(e) => setLimit(Math.max(1, parseInt(e.target.value) || 20))}
                  sx={{ width: 100 }}
                />
                <Tooltip title="Refresh Data">
                  <IconButton onClick={() => loadLoyaltyData()} color="primary">
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export CSV">
                  <IconButton onClick={handleExportCSV} color="primary">
                    <Download />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Summary Cards */}
        {loyaltyData?.summary && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Total Customers
                      </Typography>
                      <Typography variant="h4" color="primary" fontWeight="bold">
                        {formatNumber(loyaltyData.summary.total_customers)}
                      </Typography>
                    </Box>
                    <People sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ borderLeft: '4px solid', borderLeftColor: 'success.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Total Revenue
                      </Typography>
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        {formatCurrency(loyaltyData.summary.total_revenue)}
                      </Typography>
                    </Box>
                    <AttachMoney sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ borderLeft: '4px solid', borderLeftColor: 'warning.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Avg Loyalty Score
                      </Typography>
                      <Typography variant="h4" color="warning.main" fontWeight="bold">
                        {loyaltyData.summary.average_loyalty_score}
                      </Typography>
                    </Box>
                    <Star sx={{ fontSize: 40, color: 'warning.main', opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Card sx={{ borderLeft: '4px solid', borderLeftColor: 'info.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Top Tier
                      </Typography>
                      <Typography variant="h6" color="info.main" fontWeight="bold">
                        {Object.keys(loyaltyData.summary.top_tiers || {})[0] || 'N/A'}
                      </Typography>
                    </Box>
                    <Diamond sx={{ fontSize: 40, color: 'info.main', opacity: 0.7 }} />
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {loyaltyData.summary.top_tiers?.[Object.keys(loyaltyData.summary.top_tiers || {})[0]] || 0} customers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tier Distribution */}
        {loyaltyData?.summary?.top_tiers && (
          <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Customer Tier Distribution
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(loyaltyData.summary.top_tiers).map(([tier, count]) => (
                <Grid item xs={6} md={3} key={tier}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <TierBadge tier={tier} label={tier} />
                    <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                      {formatNumber(count)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Customers
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Rankings Table */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {getUnitIcon(selectedUnit)}
              <Typography variant="h5" fontWeight="bold">
                {getUnitTitle(selectedUnit)} Rankings
              </Typography>
              <Chip 
                label={`Top ${currentRankings.length} Customers`} 
                color="primary" 
                variant="outlined"
              />
            </Box>
            <Typography variant="body2" color="textSecondary">
              Total Revenue: {formatCurrency(
                currentRankings.reduce((sum, customer) => sum + (customer.total_spent || 0), 0)
              )}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading customer rankings...
              </Typography>
            </Box>
          ) : currentRankings.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Loyalty sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                No customer data found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Customer rankings will appear here as they make bookings and purchases.
              </Typography>
            </Box>
          ) : (
            <CustomerRankingsTable 
              customers={currentRankings}
              unit={selectedUnit}
              onCustomerClick={handleCustomerClick}
            />
          )}
        </Paper>

        {/* Customer Detail Dialog */}
        <CustomerDetailDialog
          open={detailDialogOpen}
          customer={selectedCustomer}
          unit={selectedUnit}
          onClose={() => setDetailDialogOpen(false)}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default CustomerLoyaltyDashboard;